import React from "react";
import { useTranslation } from "react-i18next";
import { BaseActivity } from "../../../model/activity/BaseActivity";
import styled from "styled-components";
import { faArrowRightLong } from "@fortawesome/free-solid-svg-icons/faArrowRightLong";
import { useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Spacing } from "../../../theme/Spacing";
import { AtiraIcon } from "../../AtiraIcon";
import { FontSize } from "../../Text";
import { isBlankValue } from "../utils/activityUtils";
import { useGetDiffPairRenderByKind } from "../utils/useGetDiffPairRender";
import { Flex } from "../../Flex";

const DiffRow = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  gap: ${Spacing.s};
  width: 100%;
  font-size: ${FontSize.m};
`;

const FromValue = styled.span<{ replaced: boolean }>`
  color: ${({ theme, replaced }) => theme[replaced ? "darkerSub" : "black"]};
  ${({ replaced }) => (replaced ? "text-decoration: line-through;" : "")}
  overflow-wrap: anywhere;
`;

const ToValue = styled.span`
  color: ${({ theme }) => theme.success};
  overflow-wrap: anywhere;
`;

const EmptyValue = styled.span`
  color: ${({ theme }) => theme.darkerSub};
  font-style: italic;
`;

/** The arrow is directional, so it has to turn around in RTL. */
const ArrowWrapper = styled(Flex)`
  align-items: center;
  flex-shrink: 0;

  html[dir="rtl"] & svg {
    transform: scaleX(-1);
  }
`;

type RenderDiffPairProps = {
  obj: { from?: any; to?: any };
  activity: BaseActivity;
};

export const RenderDiffPair: React.FC<RenderDiffPairProps> = ({
  obj,
  activity,
}) => {
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const { t } = useTranslation();

  const pair = useGetDiffPairRenderByKind(obj, activity, { user }) || {};
  const { from, to } = pair;

  // Some branches describe a single value rather than a change, and return no
  // `to` at all. That is different from a value that was cleared.
  const isChange = "to" in pair;

  // Nothing on either side means nothing changed that a reader can see. Older
  // activities still carry these because the recorder used to treat
  // `undefined -> null` as an edit.
  if (isBlankValue(from) && isBlankValue(to)) {
    return null;
  }

  // A single value, stated plainly rather than struck through.
  if (!isChange) {
    return (
      <DiffRow>
        <FromValue replaced={false}>{from}</FromValue>
      </DiffRow>
    );
  }

  if (isBlankValue(from)) {
    return (
      <DiffRow>
        <ToValue>{to}</ToValue>
      </DiffRow>
    );
  }

  return (
    <DiffRow>
      <FromValue replaced>{from}</FromValue>

      <ArrowWrapper>
        <AtiraIcon icon={faArrowRightLong} color="darkerSub" size="sm" />
      </ArrowWrapper>

      {/* A cleared value needs to say so - rendering the old value alone reads
          as if it were still current. */}
      {isBlankValue(to) ? (
        <EmptyValue>({t("common.none")})</EmptyValue>
      ) : (
        <ToValue>{to}</ToValue>
      )}
    </DiffRow>
  );
};
